import { Button, Input, Notification, toast } from "components/ui";
import { CompanyContext } from "contexts/CompanyContext";
import { ModalContext } from "contexts/ModalContext";
import { TrendingNewsContext } from "contexts/TrendingNewsContext";
import React, { useContext, useEffect, useState } from "react";
import { HiCheckCircle, HiOutlineMagnifyingGlass } from "react-icons/hi2";

import { useNavigate } from "react-router-dom";
import { getReadableByURL } from "utils/getReadableByURL";

import { useTrendingNews } from "utils/hooks/useTrendingNews";
import TrendingNewsItem from "views/home/TrendingNewsItem";
import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import { HiOutlineCheckCircle } from "react-icons/hi2";
import SocialNetworkSidePanel from "views/calendar/SocialNetworkSidePanel";
import { CalendarActiveSocialNetworkContext } from "contexts/CalendarActiveSocialNetworkContext";
import { socialNetworkPrompts, socialNetworkPromptsSuffix } from "components/layout/MagicEditor/functions/AdaptToSocialNetwork";
import { Loading } from "components/shared";
import { Trans, useTranslation } from "react-i18next";
import { AIContext } from "contexts/AIContext";
import InputModern from "components/ui/Input/InputModern";
import ConfigPanel from "../ConfigPanel";
import { replaceAll } from "utils/replaceAll";
import usePostGenerationQuantity from "utils/hooks/usePostGenerationQuantity";
import useQuestionsContexts from "utils/hooks/useQuestionsContexts";


const Item = ({item, isSelected, onSelect, onUnselect}) => {
    item = item?.trim();
    if (item?.[0] == "-") {
        item = item?.substring(1);
        item = item?.trim();
    }
    if (item?.split(".")?.[0]?.length < 4) {
        item = item?.split(".")?.slice(1)?.join(".")
    }
    if (!item) return <></>;
    return (
        <div 
            onClick={isSelected ? onUnselect : onSelect} 
            className={`
                transition-all 
                bg-secondary-500 
                hover:bg-primary-50 
                text-tertiary-800 
                dark:bg-gray-800 
                dark:text-gray-50 
                hover:dark:bg-gray-700 
                cursor-pointer 
                inline-block 
                p-4 
                px-5 
                rounded-2xl 
                !text-base
                transition-all 
                flex
                items-center
                gap-2.5
                ${isSelected && `
                    !bg-primary-600
                    !text-secondary-500
                    !font-semibold
                `}
            `}
        >
            <span className="flex-1">
                {item}
            </span>
            {isSelected 
                ? <HiCheckCircle className="shrink-0 text-2xl text-secondary-500" />
                : <HiOutlineCheckCircle className="shrink-0 text-2xl opacity-[0.2]" />
            }
        </div>
    );
}

const QuestionsTemplate = ({
    generatePostsFromContexts,
    config,
    setConfig
}) => {

    const perPage = 15;
    const maxSelection = usePostGenerationQuantity();

    const { company } = useContext(CompanyContext);
    const { getQuestionsContexts } = useQuestionsContexts();
    const [ query, setQuery ] = useState("");
    const [ items, setItems ] = useState([]);
    const [ selected, setSelected ] = useState([]);
    const [ isLoading, setIsLoading ] = useState(false);
    const { t } = useTranslation();

    const get = async (replace = false) => {
        if (isLoading) return;
        setIsLoading(true);
        const newItems = await getQuestionsContexts(query, perPage, items || []);
        setItems(replace ? newItems : [...(items || []), ...(newItems || [])]);
        setIsLoading(false);
    }

    const onSelect = (item) => {
        if (selected?.length >= maxSelection) {
            toast.push(<Notification title={`You can't select more than ${maxSelection} questions at once.`} type="warning" />);
            return;
        }
        setSelected([item, ...selected]);
    }

    const onUnselect = (item) => {
        setSelected(selected.filter(e => e != item))
    }

    const handleOnSubmit = async () => {
        let _selected = selected.map(e => `Relate it with the following question but do not answer it like a question: "${e}"`)
        generatePostsFromContexts(_selected);
    }

    useEffect(() => {
        if (items?.length) {
            localStorage.setItem(`questionsTemplateQuery${company?.id}`, query);
            try {
                localStorage.setItem(`questionsTemplate${company?.id}`, JSON.stringify(items));
            } catch (err) {}
        }
    }, [items]);

    useEffect(() => {
        if (!items?.length) {
            if (localStorage.getItem(`questionsTemplate${company?.id}`)) {
                setQuery(localStorage.getItem(`questionsTemplateQuery${company?.id}`));
                try {
                    setItems(JSON.parse(localStorage.getItem(`questionsTemplate${company?.id}`)));
                } catch (err) {}
            }
        }
    }, []);
    
    return (
        <div className="w-full h-full flex items-start p-4 gap-4">
            <ConfigPanel 
                selected={selected}
                onSubmit={handleOnSubmit}
                config={config}
                setConfig={setConfig}
            />
            <div className="flex-1 flex flex-col lg:flex-row gap-2 w-full h-full overflow-y-auto">
                <div className="overflow-y-auto shrink-0 flex-1">
                    <div className='relative bg-secondary-500 dark:bg-gray-700 rounded-2xl'> 
                        <InputModern
                            fieldsetClassName="!bg-transparent"
                            className="!pl-8 !px-5 !py-5 !h-auto rounded-2xl !bg-transparent !font-semibold !text-slate-800 dark:!text-slate-200" 
                            value={query}
                            placeholder={t(`questionsTemplate.placeholderQuery`)} 
                            prefix={<span className="!font-normal text-sm text-tertiary-600 dark:text-gray-200 mr-2 ml-2 !mb-2.5 inline-block">Topic: </span>}
                            onChange={(e) => setQuery(e.target.value)} 
                            onKeyUp={(e) => e.key == 'Enter' ? get(true) : void 0}
                        />
                        <Button 
                            loading={isLoading}
                            variant="twoTone" 
                            className="flex gap-2 items-center !absolute right-2.5 top-1/2 translate-y-[-50%]"
                            onClick={() => get(true)}
                            icon={<HiOutlineMagnifyingGlass />}
                        />
                    </div>
                    <div className="h-fit pb-8 mt-6">
                        <ResponsiveMasonry columnsCountBreakPoints={{350: 1, 750: 2, 900: 2, 1100: 3, 1400: 3}}>
                            <Masonry gutter='1rem'>
                                {!!items?.length && items?.map((item, key) => (  
                                    item?.trim().slice(-1) != ":" &&
                                    <Item 
                                        item={item} 
                                        isSelected={selected.filter(e => e == item)?.length > 0}
                                        onSelect={() => onSelect(item)}
                                        onUnselect={() => onUnselect(item)}
                                    />
                                ))}
                            </Masonry>
                        </ResponsiveMasonry>
                        {items?.length > 0 && !isLoading && 
                            <div className="flex items-center justify-center mt-6">
                                <Button loading={isLoading} onClick={() => get()}>
                                    <Trans i18nKey={`questionsTemplate.loadMore`}>Load more</Trans>
                                </Button>
                            </div>
                        }
                        {isLoading && 
                            <div className="flex items-center justify-center w-full mt-6">
                                <Loading type={'element'} /> 
                            </div>
                        }
                    </div>
                </div>
            </div>
        </div>
    );
}

export default QuestionsTemplate;